import type { HttpClient, HttpRequestOptions, HttpResponse } from "./types.js";
import type { CookieJar, Cookie } from "./cookie-jar.js";
import type { Logger } from "../utils/logger.js";
import type { TransportMode } from "../data-model/options.js";
import type { Url } from "../data-model/primitives.js";
import { isCloudflareChallenge } from "./cf-challenge.js";
import { hostnameOf } from "../utils/url.js";
import { DEFAULT_UA_POOL } from "./user-agents.js";

const FALLBACK_UA = DEFAULT_UA_POOL[0] ?? "Mozilla/5.0";

export interface CookieCaptureResult {
  readonly cookies: readonly Cookie[];
  readonly ua: string | null;
}

export interface TieredHttpClientOptions {
  readonly httpBranch: HttpClient;
  readonly browserBranch: HttpClient;
  readonly cookieJar: CookieJar;
  readonly mode: TransportMode;
  /** Returns the preferred transport for the adapter handling this URL. */
  readonly preferredTransport: (url: Url) => "http" | "browser";
  /** Called after each successful browser-branch call to snapshot CF cookies + UA. */
  readonly onCookieCapture: (host: string) => Promise<CookieCaptureResult>;
  readonly logger: Logger;
}

export class TieredHttpClient implements HttpClient {
  readonly #httpBranch: HttpClient;
  readonly #browserBranch: HttpClient;
  readonly #jar: CookieJar;
  readonly #mode: TransportMode;
  readonly #preferred: (url: Url) => "http" | "browser";
  readonly #onCapture: (host: string) => Promise<CookieCaptureResult>;
  readonly #logger: Logger;

  constructor(opts: TieredHttpClientOptions) {
    this.#httpBranch = opts.httpBranch;
    this.#browserBranch = opts.browserBranch;
    this.#jar = opts.cookieJar;
    this.#mode = opts.mode;
    this.#preferred = opts.preferredTransport;
    this.#onCapture = opts.onCookieCapture;
    this.#logger = opts.logger;
  }

  async get(url: Url, opts?: HttpRequestOptions): Promise<HttpResponse> {
    if (this.#mode === "browser-required") {
      return this.#callBrowser(url, opts);
    }
    if (this.#mode === "http-only") {
      return this.#httpBranch.get(url, opts);
    }
    // auto
    if (this.#preferred(url) === "browser") {
      return this.#callBrowser(url, opts);
    }
    const res = await this.#httpBranch.get(url, opts);
    if (isCloudflareChallenge(res)) {
      this.#logger.info({ url, status: res.status }, "transport: escalating to browser tier");
      return this.#callBrowser(url, opts);
    }
    return res;
  }

  async #callBrowser(url: Url, opts: HttpRequestOptions | undefined): Promise<HttpResponse> {
    const res = await this.#browserBranch.get(url, opts);
    try {
      const host = hostnameOf(url);
      const captured = await this.#onCapture(host);
      this.#jar.put(host, captured.cookies, captured.ua ?? FALLBACK_UA);
    } catch (err) {
      this.#logger.warn({ err }, "transport: cookie capture failed (non-fatal)");
    }
    return res;
  }
}
